export interface BookedItemPrice {
  total: number
  /** Stay length for accommodation. 0 for experiences, or when the order omits duration. */
  nights: number
  currency: string
}

interface PricedOrderItem {
  total: number
  status: App.OrderItemStatus
  duration?: number
}

/**
 * Sums what the customer was actually charged across the items making up one booking.
 *
 * A booking spreads across several items — one per room — so the totals are summed, while
 * nights is the longest item rather than the sum, because rooms are occupied concurrently.
 * Cancelled items are excluded: they are no longer paid for.
 */
function sumPaidItems(
  items: Array<PricedOrderItem>,
  currency: string,
): BookedItemPrice | undefined {
  const paidItems = items.filter((item) => item.status !== 'cancelled')
  if (!paidItems.length) return undefined

  return {
    total: paidItems.reduce((sum, item) => sum + item.total, 0),
    nights: Math.max(0, ...paidItems.map((item) => item.duration ?? 0)),
    currency,
  }
}

/**
 * The amount paid for one accommodation offer within an order. LE and bedbank stays live in
 * separate collections on the order, so both are matched on the offer.
 */
export function getBookedOfferPrice(
  order: App.Order | undefined,
  offerId: string,
): BookedItemPrice | undefined {
  if (!order) return undefined

  return sumPaidItems(
    [
      ...order.items.filter((item) => item.offerId === offerId),
      ...order.bedbankItems.filter((item) => item.offer.id === offerId),
    ],
    order.currencyCode,
  )
}

/** The amount paid for one experience within an order. */
export function getBookedExperiencePrice(
  order: App.Order | undefined,
  experienceId: string,
): BookedItemPrice | undefined {
  if (!order) return undefined

  return sumPaidItems(
    order.experienceItems.filter((item) => item.experienceId === experienceId),
    order.currencyCode,
  )
}
